import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@aaas/shared';
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  User,
} from 'lucide-react';
import { ReviewStatusButtons, ReviewStatusBadge } from '../../components/ReviewStatusButtons';
import shareService from '../../services/share.service';
import type { PublicCV, PublicShareData, ReviewStatus } from '../../types';

type FilterTab = 'all' | 'pass' | 'fail' | 'none';

export function PublicSharePage() {
  const { t } = useTranslation('ats');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // State
  const [shareData, setShareData] = useState<PublicShareData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  // FLIP animation refs
  const cardElsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const snapshotRef = useRef<Map<string, DOMRect>>(new Map());

  // FLIP: after DOM update, animate cards from old position to new position
  useLayoutEffect(() => {
    const snapshot = snapshotRef.current;
    if (snapshot.size === 0) return;
    snapshotRef.current = new Map();

    cardElsRef.current.forEach((el, id) => {
      const prev = snapshot.get(id);
      if (!prev) return;

      const curr = el.getBoundingClientRect();
      const dx = prev.left - curr.left;
      const dy = prev.top - curr.top;

      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      // Invert: place element at its old position
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.transition = 'none';
      el.style.zIndex = '10';

      // Play: animate to new position
      requestAnimationFrame(() => {
        el.style.transform = '';
        el.style.transition = 'transform 300ms ease-in-out';
        el.addEventListener(
          'transitionend',
          () => {
            el.style.zIndex = '';
          },
          { once: true }
        );
      });
    });
  });

  // Snapshot all card positions (called before state update)
  const snapshotPositions = useCallback(() => {
    snapshotRef.current = new Map();
    cardElsRef.current.forEach((el, id) => {
      snapshotRef.current.set(id, el.getBoundingClientRect());
    });
  }, []);

  // Fetch share data
  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await shareService.getSharedCVs(token);
        setShareData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('public.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, t]);

  // Handle CV click - navigate to detail page
  const handleCVClick = (cvId: string) => {
    navigate(`/ats/public/share/${token}/${cvId}`);
  };

  // Handle review status change (optimistic update)
  const handleReviewStatusChange = useCallback(
    async (cvId: string, newStatus: ReviewStatus) => {
      if (!token || !shareData) return;

      // Snapshot before status change (card may move between sections)
      snapshotPositions();

      // Optimistic update
      setShareData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          cvs: prev.cvs.map((cv) =>
            cv.id === cvId ? { ...cv, review_status: newStatus } : cv
          ),
        };
      });

      try {
        await shareService.updateReviewStatus(token, cvId, newStatus);
      } catch (err) {
        console.error('Failed to update review status:', err);
        // Revert on error
        snapshotPositions();
        setShareData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            cvs: prev.cvs.map((cv) =>
              cv.id === cvId ? { ...cv, review_status: cv.review_status } : cv
            ),
          };
        });
      }
    },
    [token, shareData, snapshotPositions]
  );

  // Handle reorder (swap within same review_status section)
  const handleSwap = useCallback(
    async (cvId: string, direction: 'up' | 'down') => {
      if (!token || !shareData) return;

      // Find the CV and its section peers (same review_status)
      const cv = shareData.cvs.find((c) => c.id === cvId);
      if (!cv) return;

      const sectionCvs = [...shareData.cvs]
        .filter((c) => c.review_status === cv.review_status)
        .sort((a, b) => a.order - b.order);

      const currentIndex = sectionCvs.findIndex((c) => c.id === cvId);
      const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (swapIndex < 0 || swapIndex >= sectionCvs.length) return;

      // Snapshot positions before DOM update
      snapshotPositions();

      // Swap orders between the two items
      const currentOrder = sectionCvs[currentIndex].order;
      const swapOrder = sectionCvs[swapIndex].order;

      const newCvs = shareData.cvs.map((c) => {
        if (c.id === sectionCvs[currentIndex].id) return { ...c, order: swapOrder };
        if (c.id === sectionCvs[swapIndex].id) return { ...c, order: currentOrder };
        return c;
      });

      // Optimistic update
      setShareData((prev) => (prev ? { ...prev, cvs: newCvs } : prev));

      // Send full reorder (all CVs sorted by display order)
      const allSorted = [...newCvs].sort((a, b) => a.order - b.order);
      try {
        await shareService.reorderCVs(
          token,
          allSorted.map((c) => c.id)
        );
      } catch (err) {
        console.error('Failed to reorder CVs:', err);
        snapshotPositions();
        setShareData((prev) => (prev ? { ...prev, cvs: shareData.cvs } : prev));
      }
    },
    [token, shareData, snapshotPositions]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-2 text-muted-foreground">{t('public.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !shareData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="mt-4 text-lg font-semibold">{error || t('public.invalidLink')}</h2>
              <p className="mt-2 text-muted-foreground">
                {t('public.contactSharer')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group CVs into sections: Pass → Not Reviewed → Fail, sorted by order within each
  const allCvs = [...shareData.cvs];
  const sections: { key: ReviewStatus; label: string; color: string; cvs: PublicCV[] }[] = [
    {
      key: 'pass',
      label: t('public.review.pass'),
      color: 'text-green-700',
      cvs: allCvs.filter((cv) => cv.review_status === 'pass').sort((a, b) => a.order - b.order),
    },
    {
      key: null,
      label: t('public.review.notReviewed'),
      color: 'text-muted-foreground',
      cvs: allCvs.filter((cv) => cv.review_status === null).sort((a, b) => a.order - b.order),
    },
    {
      key: 'fail',
      label: t('public.review.fail'),
      color: 'text-red-700',
      cvs: allCvs.filter((cv) => cv.review_status === 'fail').sort((a, b) => a.order - b.order),
    },
  ];

  // Filter counts
  const counts = {
    all: allCvs.length,
    pass: sections[0].cvs.length,
    fail: sections[2].cvs.length,
    none: sections[1].cvs.length,
  };

  // When a filter is active, only show that section
  const visibleSections =
    activeFilter === 'all'
      ? sections
      : activeFilter === 'none'
        ? sections.filter((s) => s.key === null)
        : sections.filter((s) => s.key === activeFilter);

  const filterTabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: t('public.review.all') },
    { key: 'pass', label: t('public.review.pass') },
    { key: 'none', label: t('public.review.notReviewed') },
    { key: 'fail', label: t('public.review.fail') },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header - Job info only */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold">{shareData.job.position_name}</h1>
              <p className="text-sm text-muted-foreground">{t('public.jobCode', { code: shareData.job.code })}</p>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{t('public.expires', { date: new Date(shareData.expires_at).toLocaleDateString('en-US') })}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* CV Grid */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeFilter === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-muted-foreground hover:bg-gray-100 border'
              }`}
            >
              {tab.label} ({counts[tab.key]})
            </button>
          ))}
        </div>

        {visibleSections.map((section) => {
          if (section.cvs.length === 0) return null;
          return (
            <div key={section.label} className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <h2 className={`text-sm font-semibold uppercase tracking-wide ${section.color}`}>
                  {section.label}
                </h2>
                <span className="text-xs text-muted-foreground">({section.cvs.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.cvs.map((cv, index) => (
                  <div
                    key={cv.id}
                    ref={(el) => {
                      if (el) cardElsRef.current.set(cv.id, el);
                      else cardElsRef.current.delete(cv.id);
                    }}
                  >
                    <CVGridCard
                      cv={cv}
                      onClick={() => handleCVClick(cv.id)}
                      onReviewStatusChange={(status) => handleReviewStatusChange(cv.id, status)}
                      onMoveUp={index > 0 ? () => handleSwap(cv.id, 'up') : undefined}
                      onMoveDown={
                        index < section.cvs.length - 1
                          ? () => handleSwap(cv.id, 'down')
                          : undefined
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {allCvs.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            {t('public.noCvs')}
          </div>
        )}
      </main>
    </div>
  );
}

// CV Grid Card Component
interface CVGridCardProps {
  cv: PublicCV;
  onClick: () => void;
  onReviewStatusChange: (status: ReviewStatus) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

function CVGridCard({ cv, onClick, onReviewStatusChange, onMoveUp, onMoveDown }: CVGridCardProps) {
  const { t } = useTranslation('ats');
  return (
    <Card
      className="h-full cursor-pointer hover:shadow-md hover:border-primary/50 transition-shadow flex flex-col"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base truncate">{cv.name}</CardTitle>
              <ReviewStatusBadge status={cv.review_status} />
            </div>
            <div className="flex flex-col gap-1 mt-1 text-sm text-muted-foreground">
              {cv.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{cv.email}</span>
                </span>
              )}
              {cv.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  {cv.phone}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2 mt-auto">
        <div className="flex items-center justify-between border-t pt-3">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              {t('public.notes', { count: cv.note_count })}
            </span>
            <span className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              {t('public.files', { count: cv.attachments.length })}
            </span>
          </div>

          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {/* Reorder buttons */}
            <div className="flex flex-col mr-1">
              {onMoveUp && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveUp();
                  }}
                  className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  title={t('public.moveUp')}
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
              )}
              {onMoveDown && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveDown();
                  }}
                  className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  title={t('public.moveDown')}
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Review status buttons */}
            <ReviewStatusButtons
              status={cv.review_status}
              onStatusChange={onReviewStatusChange}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
